import React, { useState, useEffect, useCallback } from "react";

const C = {
  bg: "#0b0e14",
  panel: "#12161f",
  card: "#181e2b",
  border: "#232b3b",
  amber: "#f59e0b",
  green: "#10b981",
  red: "#ef4444",
  cyan: "#06b6d4",
  blue: "#3b82f6",
  purple: "#8b5cf6",
  muted: "#94a3b8",
  dim: "#64748b",
  text: "#f8fafc"
};

export default function ContinuousForgeTab({ apiFetch }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [activeBasketKey, setActiveBasketKey] = useState("HYBRID_ALL_WEATHER_50_80");
  const [msg, setMsg] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await apiFetch("/api/forge/status");
      const json = await res.json();
      if (json && json.ok) {
        setData(json);
      }
    } catch (err) {
      console.error("Error fetching continuous forge status:", err);
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 15000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const handleTrigger = async () => {
    setTriggering(true);
    setMsg(null);
    try {
      const res = await apiFetch("/api/forge/trigger", { method: "POST" });
      const json = await res.json();
      if (json && json.ok) {
        setMsg("✅ Continuous Forge cycle triggered! Evaluating candidates...");
        setTimeout(fetchStatus, 3000);
      } else {
        setMsg(`❌ Trigger failed: ${json.error || "Unknown error"}`);
      }
    } catch (err) {
      setMsg(`❌ Trigger error: ${err.message}`);
    } finally {
      setTriggering(false);
    }
  };

  if (loading && !data) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: C.muted, fontFamily: "inherit" }}>
        <div style={{ fontSize: 24, marginBottom: 12 }}>⚡</div>
        <div>Loading Continuous Strategy Forge Telemetry...</div>
      </div>
    );
  }

  const state = data?.state || {};
  const target = data?.target || { weekly_trades: 50, win_rate: 80.0 };
  const synthesized = state.synthesized_baskets || {};
  const currentBasket = synthesized[activeBasketKey] || Object.values(synthesized)[0] || null;
  const staged = data?.pending_params || [];
  const logs = data?.recent_logs || [];
  const isRunning = data?.is_running;

  // Best metrics across synthesized baskets
  const bestBasket = Object.values(synthesized).reduce((prev, curr) => {
    if (!prev) return curr;
    return (curr.weighted_wr || 0) > (prev.weighted_wr || 0) ? curr : prev;
  }, null);

  return (
    <div style={{ fontFamily: "inherit", color: C.text, padding: "4px 2px 24px" }}>
      {/* ── Top Header Banner ── */}
      <div
        style={{
          background: `linear-gradient(135deg, ${C.panel} 0%, #151d2c 100%)`,
          border: `1px solid ${C.border}`,
          borderRadius: 8,
          padding: "16px 20px",
          marginBottom: 16,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12
        }}
      >
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
            <span style={{ fontSize: 20 }}>⚡</span>
            <span style={{ fontSize: 16, fontWeight: 900, letterSpacing: 1.5, color: C.amber }}>
              CONTINUOUS STRATEGY FORGE & EVOLVER
            </span>
            <span
              style={{
                fontSize: 10,
                padding: "2px 8px",
                borderRadius: 4,
                fontWeight: 700,
                background: isRunning ? "#022c22" : "#1e293b",
                color: isRunning ? C.green : C.cyan,
                border: `1px solid ${isRunning ? C.green + "44" : C.cyan + "44"}`
              }}
            >
              {isRunning ? "● RUNNING SWEEP" : "● STANDBY (CRON */20M)"}
            </span>
          </div>
          <div style={{ fontSize: 12, color: C.muted }}>
            Autonomous Multi-Asset Engine targeting <b style={{ color: C.text }}>≥50 Trades/Week</b> at <b style={{ color: C.text }}>≥80% Weekly Win Rate</b> across Synthetics & Forex.
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            onClick={handleTrigger}
            disabled={triggering || isRunning}
            style={{
              background: triggering || isRunning ? C.card : C.amber,
              color: triggering || isRunning ? C.muted : "#000",
              fontWeight: 800,
              fontSize: 12,
              padding: "8px 16px",
              borderRadius: 6,
              border: "none",
              cursor: triggering || isRunning ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6
            }}
          >
            {triggering ? "⏳ Triggering..." : isRunning ? "⚙️ In Progress..." : "⚡ Run Cycle Now"}
          </button>
          <button
            onClick={fetchStatus}
            style={{
              background: "transparent",
              color: C.dim,
              fontSize: 11,
              padding: "7px 12px",
              borderRadius: 6,
              border: `1px solid ${C.border}`,
              cursor: "pointer"
            }}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {msg && (
        <div
          style={{
            background: msg.startsWith("✅") ? "#022c22" : "#450a0a",
            border: `1px solid ${msg.startsWith("✅") ? C.green : C.red}`,
            color: msg.startsWith("✅") ? C.green : C.red,
            padding: "8px 14px",
            borderRadius: 6,
            fontSize: 12,
            marginBottom: 16
          }}
        >
          {msg}
        </div>
      )}

      {/* ── KPI Metrics Cards ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
          gap: 12,
          marginBottom: 16
        }}
      >
        {/* Card 1: Target Objective */}
        <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 8, padding: "14px 16px" }}>
          <div style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>
            🎯 Target Objective
          </div>
          <div style={{ fontSize: 20, fontWeight: 900, color: C.amber }}>
            50+ T/Wk @ 80% WR
          </div>
          <div style={{ fontSize: 11, color: C.dim, marginTop: 4 }}>
            Combined Basket across Synthetics + Forex
          </div>
        </div>

        {/* Card 2: Highest Weighted Win Rate */}
        <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 8, padding: "14px 16px" }}>
          <div style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>
            🏆 Top Basket Win Rate
          </div>
          <div style={{ fontSize: 20, fontWeight: 900, color: (bestBasket?.weighted_wr || 0) >= 80 ? C.green : C.cyan }}>
            {bestBasket ? `${bestBasket.weighted_wr.toFixed(1)}%` : "Evaluating..."}
          </div>
          <div style={{ marginTop: 6, height: 6, background: C.card, borderRadius: 3, overflow: "hidden" }}>
            <div
              style={{
                width: `${Math.min(100, ((bestBasket?.weighted_wr || 0) / 80) * 100)}%`,
                height: "100%",
                background: (bestBasket?.weighted_wr || 0) >= 80 ? C.green : C.cyan
              }}
            />
          </div>
          <div style={{ fontSize: 10, color: C.dim, marginTop: 4 }}>
            {bestBasket ? `${bestBasket.wr_progress_pct}% to 80% WR Target` : "Running sweeps"}
          </div>
        </div>

        {/* Card 3: Total Weekly Volume */}
        <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 8, padding: "14px 16px" }}>
          <div style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>
            📈 Weekly Trade Volume
          </div>
          <div style={{ fontSize: 20, fontWeight: 900, color: (bestBasket?.total_weekly_trades || 0) >= 50 ? C.green : C.amber }}>
            {bestBasket ? `${bestBasket.total_weekly_trades} T/Wk` : "0 T/Wk"}
          </div>
          <div style={{ marginTop: 6, height: 6, background: C.card, borderRadius: 3, overflow: "hidden" }}>
            <div
              style={{
                width: `${Math.min(100, ((bestBasket?.total_weekly_trades || 0) / 50) * 100)}%`,
                height: "100%",
                background: (bestBasket?.total_weekly_trades || 0) >= 50 ? C.green : C.amber
              }}
            />
          </div>
          <div style={{ fontSize: 10, color: C.dim, marginTop: 4 }}>
            {bestBasket ? `${bestBasket.total_daily_trades} Trades/Day (${bestBasket.trades_progress_pct}% of 50 Target)` : "Synthesizing basket"}
          </div>
        </div>

        {/* Card 4: Evolution Progress */}
        <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 8, padding: "14px 16px" }}>
          <div style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>
            🧬 Evolution Cycles
          </div>
          <div style={{ fontSize: 20, fontWeight: 900, color: C.purple }}>
            #{state.iteration || 0}
          </div>
          <div style={{ fontSize: 11, color: C.dim, marginTop: 4 }}>
            Candidates Evaluated: <b style={{ color: C.text }}>{state.candidates_evaluated || 0}</b>
          </div>
          <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>
            Last Sweep: {state.last_run ? new Date(state.last_run).toLocaleTimeString() : "Pending"}
          </div>
        </div>
      </div>

      {/* ── Synthesized Multi-Asset Baskets Showcase ── */}
      <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 8, padding: 16, marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: 1.2, color: C.amber }}>
              SYNTHESIZED MULTI-ASSET BASKETS (GOAL: 50+ T/WK @ 80% WR)
            </div>
            <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>
              Automatic aggregation of high-edge symbols to reach required frequency and edge.
            </div>
          </div>

          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {Object.keys(synthesized).map((key) => {
              const b = synthesized[key];
              const isAct = activeBasketKey === key;
              return (
                <button
                  key={key}
                  onClick={() => setActiveBasketKey(key)}
                  style={{
                    background: isAct ? C.card : "transparent",
                    border: `1px solid ${isAct ? C.amber : C.border}`,
                    color: isAct ? C.amber : C.muted,
                    padding: "5px 12px",
                    borderRadius: 4,
                    fontSize: 11,
                    fontWeight: isAct ? 800 : 500,
                    cursor: "pointer"
                  }}
                >
                  {b.name.replace("KARA_", "").replace("_50_80", "")} ({b.symbol_count || 0})
                </button>
              );
            })}
          </div>
        </div>

        {currentBasket && (
          <div>
            {/* Basket Summary Row */}
            <div
              style={{
                background: C.card,
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                padding: "10px 14px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 10,
                marginBottom: 12
              }}
            >
              <div>
                <span style={{ fontSize: 12, fontWeight: 800, color: C.text }}>{currentBasket.name}</span>
                <span style={{ fontSize: 10, color: C.cyan, marginLeft: 8, padding: "2px 6px", background: "#083344", borderRadius: 3 }}>
                  {currentBasket.type}
                </span>
              </div>
              <div style={{ display: "flex", gap: 16, fontSize: 12 }}>
                <div>
                  <span style={{ color: C.muted }}>Weekly Volume: </span>
                  <b style={{ color: currentBasket.total_weekly_trades >= 50 ? C.green : C.amber }}>
                    {currentBasket.total_weekly_trades} T/Wk
                  </b>
                  <span style={{ color: C.dim, fontSize: 10 }}> ({currentBasket.total_daily_trades} TPD)</span>
                </div>
                <div>
                  <span style={{ color: C.muted }}>Weighted WR: </span>
                  <b style={{ color: currentBasket.weighted_wr >= 80 ? C.green : C.cyan }}>
                    {currentBasket.weighted_wr}%
                  </b>
                </div>
                <div>
                  <span style={{ color: C.muted }}>Goal Status: </span>
                  <b style={{ color: currentBasket.target_met ? C.green : C.amber }}>
                    {currentBasket.target_met ? "🎯 TARGET MET" : "⚡ EVOLVING"}
                  </b>
                </div>
              </div>
            </div>

            {/* Per-Symbol Breakdown Table */}
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, textAlign: "left" }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${C.border}`, color: C.muted }}>
                    <th style={{ padding: "6px 8px" }}>SYMBOL</th>
                    <th style={{ padding: "6px 8px" }}>ASSET</th>
                    <th style={{ padding: "6px 8px" }}>STRATEGY & ENGINE</th>
                    <th style={{ padding: "6px 8px" }}>WIN RATE</th>
                    <th style={{ padding: "6px 8px" }}>WEEKLY TRADES</th>
                    <th style={{ padding: "6px 8px" }}>TRADES/DAY</th>
                    <th style={{ padding: "6px 8px" }}>PEAK HOURS (WHITELIST)</th>
                    <th style={{ padding: "6px 8px" }}>PROFIT FACTOR</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(currentBasket.symbols || {}).map(([sym, c]) => (
                    <tr key={sym} style={{ borderBottom: `1px solid ${C.border}44` }}>
                      <td style={{ padding: "8px 8px", fontWeight: 800, color: C.amber }}>{sym}</td>
                      <td style={{ padding: "8px 8px", color: c.is_forex ? C.blue : C.purple }}>
                        {c.is_forex ? "Forex" : "Synthetic"}
                      </td>
                      <td style={{ padding: "8px 8px", color: C.muted }}>
                        {c.strategy.replace(".md", "")} <span style={{ color: C.dim }}>({c.engine})</span>
                      </td>
                      <td style={{ padding: "8px 8px", fontWeight: 700, color: c.wr >= 70 ? C.green : C.cyan }}>
                        {c.wr}%
                      </td>
                      <td style={{ padding: "8px 8px", fontWeight: 700, color: C.text }}>
                        {c.weekly_trades} T/Wk
                      </td>
                      <td style={{ padding: "8px 8px", color: C.muted }}>{c.trades_per_day}</td>
                      <td style={{ padding: "8px 8px", fontFamily: "monospace", color: C.cyan, fontSize: 10 }}>
                        {c.hour_whitelist && c.hour_whitelist.length > 0 ? `[${c.hour_whitelist.join(", ")}]` : "All Hours"}
                      </td>
                      <td style={{ padding: "8px 8px", color: (c.metrics?.profit_factor || 1) >= 1.5 ? C.green : C.muted }}>
                        {c.metrics?.profit_factor ? c.metrics.profit_factor.toFixed(2) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* ── Staged Candidates Queue (pending_params.json) ── */}
      <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 8, padding: 16, marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: 1.2, color: C.cyan }}>
              STAGED HIGH-FREQUENCY CANDIDATES ({staged.length})
            </div>
            <div style={{ fontSize: 11, color: C.dim }}>
              Candidates verified by WFA & Monte Carlo, staged in <code>pending_params.json</code> for governance.
            </div>
          </div>
        </div>

        {staged.length === 0 ? (
          <div style={{ fontSize: 11, color: C.dim, padding: "12px 0" }}>No candidates currently pending in queue.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, textAlign: "left" }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.border}`, color: C.muted }}>
                  <th style={{ padding: "6px 8px" }}>STRATEGY</th>
                  <th style={{ padding: "6px 8px" }}>ENGINE</th>
                  <th style={{ padding: "6px 8px" }}>COMBINED WR</th>
                  <th style={{ padding: "6px 8px" }}>WEEKLY TRADES</th>
                  <th style={{ padding: "6px 8px" }}>TRADES/DAY</th>
                  <th style={{ padding: "6px 8px" }}>WFA STATUS</th>
                  <th style={{ padding: "6px 8px" }}>STATUS</th>
                </tr>
              </thead>
              <tbody>
                {staged.map((cand, idx) => (
                  <tr key={idx} style={{ borderBottom: `1px solid ${C.border}44` }}>
                    <td style={{ padding: "8px 8px", fontWeight: 700, color: C.amber }}>
                      {cand.strategy || cand.strategy_target || "BASKET_CANDIDATE"}
                    </td>
                    <td style={{ padding: "8px 8px", color: C.muted }}>{cand.engine || "URUS_88"}</td>
                    <td style={{ padding: "8px 8px", fontWeight: 700, color: (cand.combined_wr || 0) >= 65 ? C.green : C.cyan }}>
                      {cand.combined_wr != null ? `${Number(cand.combined_wr).toFixed(1)}%` : "—"}
                    </td>
                    <td style={{ padding: "8px 8px", color: C.text }}>
                      {cand.estimated_weekly_trades != null ? `${cand.estimated_weekly_trades} T/Wk` : "—"}
                    </td>
                    <td style={{ padding: "8px 8px", color: C.muted }}>
                      {cand.combined_tpd != null ? cand.combined_tpd : "—"}
                    </td>
                    <td style={{ padding: "8px 8px", color: cand.wfa_status === "VALIDATED" ? C.green : C.amber }}>
                      {cand.wfa_status || "VALIDATED"}
                    </td>
                    <td style={{ padding: "8px 8px" }}>
                      <span
                        style={{
                          fontSize: 9,
                          padding: "2px 6px",
                          borderRadius: 3,
                          fontWeight: 700,
                          background: "#083344",
                          color: C.cyan,
                          border: `1px solid ${C.cyan}44`
                        }}
                      >
                        {cand.staging_status || "STAGED"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Real-Time Continuous Forge Terminal Log ── */}
      <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 8, padding: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: 1.2, color: C.muted }}>
            LIVE OPTIMIZATION & CERTIFICATION LOG
          </div>
          <span style={{ fontSize: 10, color: C.dim }}>Showing last {logs.length} operations</span>
        </div>
        <div
          style={{
            background: "#070a0f",
            border: `1px solid ${C.border}`,
            borderRadius: 6,
            padding: 12,
            fontFamily: "monospace",
            fontSize: 11,
            color: "#38bdf8",
            maxHeight: 240,
            overflowY: "auto",
            lineHeight: 1.6
          }}
        >
          {logs.length === 0 ? (
            <div style={{ color: C.dim }}>No log entries found.</div>
          ) : (
            logs.map((line, idx) => (
              <div key={idx} style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                {line}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
